import type { ImportAddressFix } from "@adva/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/v2/empty-state";
import { formatNormalizedAddress, precisionLabel } from "../labels";

interface AddressFixesTabProps {
  fixes: ImportAddressFix[];
  unchecked: Set<string>;
  onToggle: (locationId: string) => void;
  onAcceptAll: () => void;
  disabled?: boolean;
}

function PrecisionBadge({ precision }: { precision: string | null }) {
  const tone =
    precision === "rooftop"
      ? "success"
      : precision === "approximate" || precision === "centroid"
        ? "warning"
        : precision === "failed"
          ? "danger"
          : "neutral";
  const colorMap: Record<typeof tone, string> = {
    success:
      "border-[color:var(--color-success)] text-[color:var(--color-success)]",
    warning:
      "border-[color:var(--color-warning)] text-[color:var(--color-warning)]",
    danger:
      "border-[color:var(--color-danger)] text-[color:var(--color-danger)]",
    neutral:
      "border-[color:var(--color-border)] text-[color:var(--color-text-md)]",
  };
  return (
    <span
      className={`inline-flex items-center rounded-sm border bg-transparent px-1.5 py-0.5 typo-label ${colorMap[tone]}`}
    >
      {precisionLabel(precision)}
    </span>
  );
}

export function AddressFixesTab({
  fixes,
  unchecked,
  onToggle,
  onAcceptAll,
  disabled = false,
}: AddressFixesTabProps) {
  if (fixes.length === 0) {
    return (
      <EmptyState
        title="No address corrections needed"
        description="None of the imported records had addresses that required normalization."
      />
    );
  }

  const allAccepted = unchecked.size === 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="typo-label text-[color:var(--color-text-md)]">
          {fixes.length} address{" "}
          {fixes.length === 1 ? "correction" : "corrections"}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={onAcceptAll}
          disabled={disabled || allAccepted}
        >
          Accept all address fixes
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <span className="sr-only">Approve</span>
            </TableHead>
            <TableHead>Original</TableHead>
            <TableHead>Normalized</TableHead>
            <TableHead className="w-32">Precision</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fixes.map((fix) => {
            const locId = fix.location_id;
            const isUnchecked = locId ? unchecked.has(locId) : false;
            const isChecked = !isUnchecked;
            return (
              <TableRow
                key={fix.id}
                className={isUnchecked ? "opacity-60" : undefined}
              >
                <TableCell>
                  <Checkbox
                    checked={isChecked}
                    disabled={disabled || !locId}
                    onCheckedChange={() => locId && onToggle(locId)}
                    aria-label={
                      isChecked ? "Approve this address fix" : "Revert"
                    }
                  />
                </TableCell>
                <TableCell className="font-mono text-[color:var(--color-text-md)]">
                  {fix.original_input}
                </TableCell>
                <TableCell>
                  <div>{formatNormalizedAddress(fix)}</div>
                  {fix.formatted_address && (
                    <div className="typo-label mt-0.5 text-[color:var(--color-text-lo)]">
                      {fix.formatted_address}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <PrecisionBadge precision={fix.precision_type} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
